import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThemeProvider } from "@mui/system";
import { Tooltip, IconButton, Box } from "@mui/material";
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import DeleteIcon from "@mui/icons-material/Delete";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import EditCalendarRoundedIcon from "@mui/icons-material/EditCalendarRounded";

// Local imports
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import GenericRnDListing from "../GenericRnDListing/GenericRnDListing";
import { setSnackbar } from "@Redux/snackBarSlice";
import { DeletePopup } from "@Components/Popup/DeletePopup";
import axiosInstance from "@API/axios";
import { setData } from "@Redux/requestDataSlice";
import { grey } from "@mui/material/colors";
import {
  stringCustomOperators, numberCustomOperators
} from "@Utils/Validations/HelperFunctions/CustomFilters";

// ===================== CONSTANTS - URLs & STRINGS =====================
// API endpoints
const RD_EXIT_PLANS_USER = "/exit-plans/user";
const EXIT_PLANS_DELETE_ENDPOINT = "/exit-plans/";

// UI text constants
const PAGE_TITLE = "Exit Plans";
const ERROR_MESSAGE = "Failed to fetch exit plans. Trying again!";
const DELETE_SUCCESS_MESSAGE = "Exit plan deleted successfully!";
const DELETE_ERROR_MESSAGE = "Failed to delete exit plan.";

// Tooltip text
const TOOLTIP_VIEW_DETAILS = "View Plan Details";
const TOOLTIP_DELETE = "Delete Plan";

// Column headers
const COLUMN_HEADERS = {
  CREATED_AT: "Created At",
  LAST_UPDATED: "Last Updated",
  STATUS: "Status",
  APPROVED: "Approved",
  CATEGORY: "Category",
  INGREDIENT: "Ingredient",
  APPROVED_BY: "Approved By",
  APPROVAL_DATE: "Approval Date",
  MARKETING_SUPPORT: "Marketing Support",
  MARKETING_SUPPORT_PROVIDED: "Marketing Support Provided",
  SUPPLY_CHAIN_SUPPORT: "Supply Chain Support",
  SUPPLY_CHAIN_SUPPORT_PROVIDED: "Supply Chain Support Provided",
  PRODUCTS: "Products",
  ACTIONS: "Actions",
};

// Default values
const DEFAULT_VALUE = "-";
const UNIT_SEPARATOR = "\u001F";
const DELETE_POPUP_ENTITY = "exit plan";
const DELETE_BUTTON_TEXT = "Delete";

// Date format for display
const DATE_FORMAT = "YYYY-MM-DD";

// ===================== HELPER FUNCTIONS =====================
/**
 * Formats a date string to locale date format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date or default value
 */
const formatDate = (dateString) => {
  return dateString ? new Date(dateString).toLocaleDateString() : DEFAULT_VALUE;
};

/**
 * Safely accesses nested object properties
 * @param {object} obj - Object to access
 * @param {string} property - Property name
 * @returns {any} Property value or default value
 */
const getValueOrDefault = (obj, property) => {
  return obj?.[property] || DEFAULT_VALUE;
};

// ===================== MAIN COMPONENT =====================
function ExitPlansListing() {
  const dispatch = useDispatch();
  const currentUserRole = useSelector((state) => state.user.role);

  // ===== Component State =====
  const [deletePopupOpen, setDeletePopupOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [rows, setRows] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ===== Column Configuration =====
  /**
   * DataGrid column definitions for ExitPlanResponseDto
   */
  const columns = [
    {
      field: "ingredientName",
      headerName: "Ingredient Name",
      flex: 1.5,
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "ingredient",
      headerName: COLUMN_HEADERS.INGREDIENT,
      flex: 1.2,
      type: "string",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div>
          <div>{params.row.ingredientName}</div>
          <div style={{ fontSize: "0.75rem", color: "gray" }}>
            CAS: {params.row.ingredientCasNumber}
          </div>
        </div>
      ),
      filterOperators: stringCustomOperators,
    },
    {
      field: "ingredientCasNumber",
      headerName: "CAS Number",
      flex: 1.2,
      type: "string",
      valueGetter: (params) => params.row.ingredientCasNumber,
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "categoryName",
      headerName: COLUMN_HEADERS.CATEGORY,
      flex: 1.3,
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "createdAt",
      headerName: COLUMN_HEADERS.CREATED_AT,
      flex: 1,
      valueGetter: (params) => {
        const dateStr = params.row.createdAt;
        return dateStr ? new Date(dateStr) : null;
      },
      valueFormatter: (params) =>
        params.value ? dayjs(params.value).format(DATE_FORMAT) : DEFAULT_VALUE,
      type: "date",
    },
    {
      field: "status",
      headerName: COLUMN_HEADERS.STATUS,
      flex: 1.5,
      type: "string",
      filterOperators: stringCustomOperators,
    },
    {
      field: "approved",
      headerName: COLUMN_HEADERS.APPROVED,
      flex: 1,
      type: "boolean",
      sortable: false,
    },
    {
      field: "approvedByEmail",
      headerName: COLUMN_HEADERS.APPROVED_BY,
      flex: 1.2,
      valueGetter: (params) =>
        getValueOrDefault(params.row.approvedBy, "email"),
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "approvalDate",
      headerName: COLUMN_HEADERS.APPROVAL_DATE,
      flex: 1.2,
      valueGetter: (params) => {
        const dateStr = params.row.approvalDate;
        return dateStr ? new Date(dateStr) : null;
      },
      valueFormatter: (params) =>
        params.value ? dayjs(params.value).format(DATE_FORMAT) : DEFAULT_VALUE,
      type: "date",
    },
    {
      field: "marketingSupport",
      headerName: COLUMN_HEADERS.MARKETING_SUPPORT,
      flex: 2,
      type: "boolean",
      sortable: false,
    },
    {
      field: "marketingSupportProvided",
      headerName: COLUMN_HEADERS.MARKETING_SUPPORT_PROVIDED,
      flex: 2,
      type: "boolean",
      sortable: false,
    },
    {
      field: "supplyChainSupport",
      headerName: COLUMN_HEADERS.SUPPLY_CHAIN_SUPPORT,
      flex: 2,
      type: "boolean",
      sortable: false,
    },
    {
      field: "supplyChainSupportProvided",
      headerName: COLUMN_HEADERS.SUPPLY_CHAIN_SUPPORT_PROVIDED,
      flex: 2,
      type: "boolean",
      sortable: false,
    },
    {
      field: "products",
      headerName: COLUMN_HEADERS.PRODUCTS,
      flex: 3,
      renderCell: (params) => <ProductsCell products={params.row.products} />,
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
      filterable: false,
    },
    {
      field: "actions",
      headerName: COLUMN_HEADERS.ACTIONS,
      width: 120,
      minWidth: 120,
      maxWidth: 120,
      disableReorder: true,
      sortable: false,
      filterable: false,
    },
  ];

  /**
   * Initial column visibility configuration
   * Only shows key columns by default
   */
  const initialColumnVisibilityModel = {
    status: true,
    products: true,
    createdAt: true,
    lastUpdatedAt: true,
    additionalComments: false,
    exitDate: false,
    replacementPlan: false,
    endOfProductionDate: false,
    endOfImportationDate: false,
    liquidationPlan: false,
    marketingSupport: false,
    supplyChainSupport: false,
    marketingSupportProvided: false,
    supplyChainSupportProvided: false,
    supportUsers: false,
    comments: false,
    approved: false,
    approvedBy: false,
    approvalDate: false,
    approvalComment: false,
    actions: true,
    ingredientCasNumber: false,
    ingredientName: false,
  };
  // ===== Event Handlers =====
  /**
   * Handles delete button click
   */
  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeletePopupOpen(true);
  };

  /**
   * Handles closing the delete popup
   */
  const handleClosePopup = () => {
    setDeletePopupOpen(false);
    setIdToDelete(null);
  };

  /**
   * Handles confirming the delete action
   * Updates both local state and global Redux state
   */
  const handleConfirmDelete = async (id) => {
    setDeleteLoading(true);

    try {
      await axiosInstance.delete(`${EXIT_PLANS_DELETE_ENDPOINT}${id}`);

      // Update local state
      setRows((prevRows) => prevRows.filter((row) => row.id !== id));

      // Update global state
      dispatch((dispatch, getState) => {
        const current = getState().requestData.requestData;
        if (current && Array.isArray(current.exitPlans)) {
          const updatedExitPlans = current.exitPlans.filter(
            (plan) => plan.id !== id
          );
          dispatch(
            setData({
              ...current,
              exitPlans: updatedExitPlans,
            })
          );
        }
      });

      // Show success message
      dispatch(
        setSnackbar({
          open: true,
          message: DELETE_SUCCESS_MESSAGE,
          error: false,
        })
      );

      handleClosePopup();
    } catch (error) {
      dispatch(
        setSnackbar({
          open: true,
          message: DELETE_ERROR_MESSAGE,
          error: true,
        })
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  /**
   * Handles navigation to plan details
   */
  const handleViewDetails = (row, navigate) => {
    const createRoute = currentUserRole === "RD_MANAGER"
      ? DefaultRoutes.rdManagerExitPlanCreate
      : DefaultRoutes.rdExitPlanCreate;
    navigate(
      createRoute
        .replace(":reqId", row.requestId)
        .replace(":categoryAssignmentId", row.categoryAssignmentId),
      { state: { planId: row.id } }
    );
  };

  // ===== Render Methods =====
  /**
   * Renders products cell with tags
   */
  const ProductsCell = ({ products }) => {
    if (!products || products.length === 0) return null;

    const items = products.split(UNIT_SEPARATOR);

    return (
      <TagsContainer>
        {items.map((item, index) => (
          <TagItem key={index}>{item}</TagItem>
        ))}
      </TagsContainer>
    );
  };

  /**
   * Renders action buttons for each row
   */
  const PLAN_VIEW_ONLY_STATUSES = ["SUBMITTED", "UNDER_REVIEW"];

  const renderActions = (params, navigate, theme) => (
    <ThemeProvider theme={theme}>
      <Tooltip title={TOOLTIP_VIEW_DETAILS}>
        <IconButton
          color="info"
          onClick={() => handleViewDetails(params.row, navigate)}
          size="small"
        >
          <LightbulbOutlinedIcon />
        </IconButton>
      </Tooltip>
      {!PLAN_VIEW_ONLY_STATUSES.includes(params.row.status) && (
        <Tooltip title={TOOLTIP_DELETE}>
          <IconButton
            color="error"
            onClick={() => handleDeleteClick(params.row.id)}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      )}
    </ThemeProvider>
  );

  // ===== Main Render =====
  return (
    <>
      <GenericRnDListing
        apiRoute={RD_EXIT_PLANS_USER}
        PageIcon={<EditCalendarRoundedIcon color="white" fontSize="large" />}
        pageTitle={PAGE_TITLE}
        columns={columns}
        renderActions={renderActions}
        errorMessage={ERROR_MESSAGE}
        initialColumnVisibilityModel={initialColumnVisibilityModel}
        listLabel={{ listName: "products", listAttr: "productName" }}
        serverPagination={true}
        rows={rows}
        setRows={setRows}
      />
      {DeletePopup(
        DELETE_POPUP_ENTITY,
        deletePopupOpen,
        handleClosePopup,
        handleConfirmDelete,
        idToDelete,
        deleteLoading,
        DELETE_BUTTON_TEXT
      )}
    </>
  );
}

// ===================== STYLED COMPONENTS =====================
/**
 * Container for product tags with horizontal scrolling
 */
export const TagsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  maxWidth: "100%",
  overflowX: "auto",
  whiteSpace: "nowrap",
  borderRadius: theme.spacing(0.5),
  position: "relative",
  "&::-webkit-scrollbar": {
    height: "0",
  },
}));

/**
 * Individual tag item style
 */
export const TagItem = styled("div")(({ theme }) => ({
  padding: theme.spacing(0.375, 1),
  border: `1.2px solid ${theme.palette.grey.medium}`,
  borderRadius: theme.spacing(2),
  fontSize: "0.875rem",
  backgroundColor: grey[300],
}));

export default ExitPlansListing;
